import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Section = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h2`
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

const UploadArea = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.02);
  
  &:hover {
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.1);
  }
  
  &.dragging {
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.2);
  }
`;

const UploadText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const UploadButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const MediaItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    border-color: #667eea;
  }
  
  &.selected {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.5);
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
`;

const MediaInfo = styled.div`
  padding: 0.75rem;
`;

const MediaName = styled.div`
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MediaSize = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #667eea;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #f56565;
  background: rgba(245, 101, 101, 0.1);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid rgba(245, 101, 101, 0.3);
`;

const SuccessMessage = styled.div`
  color: #48bb78;
  background: rgba(72, 187, 120, 0.1);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid rgba(72, 187, 120, 0.3);
`;

function MediaManagement({ onImageSelect, selectedImage }) {
  const [uploading, setUploading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragging, setDragging] = useState(false);

  // Load existing media files (for now, we'll use a simple array)
  useEffect(() => {
    // In a real implementation, you would fetch this from an API
    // For now, we'll start with an empty array
    setMediaFiles([]);
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only image files (JPG, PNG, GIF, WebP) are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('media', file);

      const response = await fetch(`${API_BASE_URL}/api/v1/upload/media`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Image uploaded successfully!');
        
        // Add to media files list
        const newMediaFile = {
          id: Date.now(),
          url: result.url,
          fileName: result.fileName,
          fileSize: result.fileSize,
          mimeType: result.mimeType,
          uniquePath: result.uniquePath
        };
        
        setMediaFiles(prev => [newMediaFile, ...prev]);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleMediaClick = (mediaFile) => {
    if (onImageSelect) {
      onImageSelect(mediaFile);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Section>
      <Title>Media Library</Title>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <UploadArea
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={dragging ? 'dragging' : ''}
        onClick={() => document.getElementById('file-input').click()}
      >
        <UploadText>
          Drag and drop an image here, or click to select
        </UploadText>
        <UploadButton disabled={uploading}>
          {uploading ? <LoadingSpinner /> : 'Choose Image'}
        </UploadButton>
        <input
          id="file-input"
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={uploading}
        />
      </UploadArea>

      <MediaGrid>
        {mediaFiles.map((mediaFile) => (
          <MediaItem
            key={mediaFile.id}
            onClick={() => handleMediaClick(mediaFile)}
            className={selectedImage?.url === mediaFile.url ? 'selected' : ''}
          >
            <MediaImage src={mediaFile.url} alt={mediaFile.fileName} />
            <MediaInfo>
              <MediaName>{mediaFile.fileName}</MediaName>
              <MediaSize>{formatFileSize(mediaFile.fileSize)}</MediaSize>
            </MediaInfo>
          </MediaItem>
        ))}
      </MediaGrid>
    </Section>
  );
}

export default MediaManagement;
